import * as React from 'react';
import {connect} from 'react-redux';

import {useQuery} from '@apollo/react-hooks';

import {makeDataDangerous} from 'nutshell-core/utils/graphql-errors';

import type {Reactions} from 'shells/timeline/reaction-bar/types';

import type {ReactionType} from 'nutshell-graphql-types';
import type {
    GetChangeLogEntry as GetChangeLogEntryQuery,
    GetChangeLogEntryVariables as GetChangeLogEntryQueryVariables,
    EventFragmentFull,
    SessionUser,
} from 'nutshell-graphql-types';

import {GetSessionUser} from '../../core/get-session-user';
import {openReplyModal} from '../../email/email-reply-actions';

import {TimelineModalEmailInteraction} from '../banner/timeline-modal-email-interaction';
import {TimelineModalOutreachSent} from '../banner/timeline-modal-outreach-sent';
import {EmailMutationWrapper} from '../comments/graphql/email-mutation-wrapper';
import {useCreateComment, useDeleteComment} from '../comments/timeline-comment-mutations';
import GET_ENTRY from '../graphql/queries/get-change-log-entry.graphql';
import {useDeleteEventForModal} from '../graphql/timeline-event-mutations';
import {getFilteredReactions, getToggleAction} from '../reactions/helpers';
import {useToggleReaction} from '../reactions/reaction-mutations';

import {TimelineChatModal} from './timeline-chat-modal';
import {TimelineEmailModal} from './timeline-email-modal';
import {TimelineNoteModal} from './timeline-note-modal';
import {TimelineSessionModal} from './timeline-session-modal';

type OwnProps = {
    id: string;
    isOpen: boolean;
    onClose: () => void;
};
type DispatchProps = {
    onOpenReplyModal: (replyToEmailId: string, isReplyingAll: boolean) => void;
};

type ConnectedProps = OwnProps & DispatchProps;

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onOpenReplyModal: (replyToEmailId: string, isReplyingAll: boolean) => {
            dispatch(openReplyModal({replyToEmailId, isReplyingAll}));
        },
    };
}

type Props = ConnectedProps & {
    event: EventFragmentFull;
    user: SessionUser;
    isLoading: boolean;
    retryFailedEmail: (arg1: string) => Promise<any>;
    updateEmail: (arg1: string, arg2: boolean) => Promise<any>;
    onDeleteEmail: (arg1: string, arg2?: string | null | undefined) => Promise<any>;
    onAddComment: (value: string) => Promise<any>;
    onDeleteComment: (commentId: string) => Promise<any>;
    onDeleteEvent: (arg1: string) => Promise<any>;
};

export function TimelineModalRootComponent(props: Props) {
    const {
        isOpen,
        onClose,
        event,
        user,
        isLoading,
        retryFailedEmail,
        updateEmail,
        onAddComment,
        onDeleteComment,
        onDeleteEvent,
        onDeleteEmail,
        onOpenReplyModal,
    } = props;

    const [isViewingAttachments, setIsViewingAttachments] = React.useState<boolean>(false);

    const toggleReaction = useToggleReaction();

    const filteredEntryReactions = getFilteredReactions(event.reactions);

    const onToggleReaction = (
        changeLogId: string,
        reactionType: ReactionType,
        filteredReactions: Reactions,
        parentChangeLogId?: string
    ) => {
        return toggleReaction({
            changeLogId,
            reactionType,
            user,
            toggleAction: getToggleAction(reactionType, filteredReactions, user.id),
            parentChangeLogId,
        });
    };

    const onReply = () =>
        event.payload.__typename === 'Email'
            ? onOpenReplyModal(event.payload.id, false)
            : undefined;
    const onReplyAll = () =>
        event.payload.__typename === 'Email' ? onOpenReplyModal(event.payload.id, true) : undefined;

    return (
        <>
            {event.payload.__typename === 'Email' && event.payload.isNutshellMarketingEmail ? (
                <TimelineModalOutreachSent
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                />
            ) : undefined}
            {event.payload.__typename === 'Email' && !event.payload.isNutshellMarketingEmail ? (
                <TimelineEmailModal
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                    currentUser={user}
                    retryFailedEmail={retryFailedEmail}
                    updateEmail={updateEmail}
                    onToggleReaction={onToggleReaction}
                    filteredEntryReactions={filteredEntryReactions}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    onDeleteEmail={onDeleteEmail}
                    onDeleteEvent={onDeleteEvent}
                    isViewingAttachments={isViewingAttachments}
                    setIsViewingAttachments={setIsViewingAttachments}
                    onReply={onReply}
                    onReplyAll={onReplyAll}
                />
            ) : undefined}
            {event.payload.__typename === 'Chat' ? (
                <TimelineChatModal
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                    currentUserId={user.id}
                    onToggleReaction={onToggleReaction}
                    filteredEntryReactions={filteredEntryReactions}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    onDeleteEvent={onDeleteEvent}
                />
            ) : undefined}
            {event.payload.__typename === 'Note' ? (
                <TimelineNoteModal
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                    currentUserId={user.id}
                    onToggleReaction={onToggleReaction}
                    filteredEntryReactions={filteredEntryReactions}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    onDeleteEvent={onDeleteEvent}
                />
            ) : undefined}
            {event.payload.__typename === 'Session' ? (
                <TimelineSessionModal
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                />
            ) : undefined}
            {event.payload.__typename === 'EmailInteraction' ? (
                <TimelineModalEmailInteraction
                    isOpen={isOpen}
                    onClose={onClose}
                    isLoading={isLoading}
                    event={event}
                />
            ) : undefined}
        </>
    );
}

function TimelineModalRootConnected(props: ConnectedProps) {
    const {id, isOpen, onClose} = props;
    const createComment = useCreateComment();
    const deleteComment = useDeleteComment();
    const deleteEvent = useDeleteEventForModal();

    const {data, loading: isLoading} = useQuery<
        GetChangeLogEntryQuery,
        GetChangeLogEntryQueryVariables
    >(GET_ENTRY, {
        variables: {id: id},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const {event} = makeDataDangerous(data);

    if (!event) {
        return null;
    }

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                if (user) {
                    return (
                        <EmailMutationWrapper>
                            {({updateEmail, retryFailedEmail, deleteEmail}) => (
                                <TimelineModalRootComponent
                                    id={id}
                                    isOpen={isOpen}
                                    onClose={onClose}
                                    event={event}
                                    isLoading={isLoading}
                                    user={user}
                                    retryFailedEmail={retryFailedEmail}
                                    updateEmail={updateEmail}
                                    onAddComment={(value) =>
                                        createComment({
                                            changeLogId: event.id,
                                            parentId: event.payload.id,
                                            value,
                                            user,
                                        })
                                    }
                                    onDeleteComment={(commentId) =>
                                        deleteComment({changeLogId: event.id, commentId})
                                    }
                                    onDeleteEmail={deleteEmail}
                                    onDeleteEvent={() => deleteEvent({changeLogId: event.id})}
                                    onOpenReplyModal={props.onOpenReplyModal}
                                />
                            )}
                        </EmailMutationWrapper>
                    );
                }

                return null;
            }}
        </GetSessionUser>
    );
}

// @ts-expect-error
const connector = connect<_, OwnProps, _, _, _, _>(null, mapDispatchToProps);
export const TimelineModalRoot = connector(TimelineModalRootConnected);
